import React, { useState, useEffect } from "react";
import { Box, Container, Typography, Paper, Fade, Zoom, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as MuiIcons from "@mui/icons-material";
import { TrendingUp } from "@mui/icons-material";
import { cmsService, CMSProcessStep } from '../services/cms';
import { useThemeMode } from '../contexts/ThemeContext';
import PageTitle from './PageTitle';

const OurProcess: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { mode } = useThemeMode();
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  const [selectedStep, setSelectedStep] = useState<number>(0);
  const [processSteps, setProcessSteps] = useState<CMSProcessStep[]>([]);
  const [loading, setLoading] = useState(true);
  
  const isRTL = i18n.dir() === 'rtl';

  const fetchProcessSteps = async () => {
    try {
      const { data, error } = await cmsService.getProcessSteps(true); // Only get active steps
      if (!error && data && data.length > 0) {
        // Translate CMS data
        const translatedSteps = data.map((step, index) => ({
          ...step,
          title: t(`ourProcess.cms.${index}.title`, { defaultValue: step.title }),
          description: t(`ourProcess.cms.${index}.description`, { defaultValue: step.description }),
          highlight_text: step.highlight_text ? t(`ourProcess.cms.${index}.highlight`, { defaultValue: step.highlight_text }) : undefined,
        }));
        setProcessSteps(translatedSteps);
      }
    } catch (error) {
      console.error('Error fetching process steps:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProcessSteps();

    // Subscribe to real-time updates
    const subscription = cmsService.subscribeToChanges('cms_process_steps', () => {
      fetchProcessSteps();
    });

    return () => {
      cmsService.unsubscribe(subscription);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // Get Material UI icon component by name
  const getIconComponent = (iconName: string) => {
    const IconComponent = (MuiIcons as any)[iconName] || MuiIcons.Help;
    return <IconComponent sx={{ fontSize: 40 }} />;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Fade in timeout={800}>
        <Box>
          <PageTitle
            text={t('ourProcess.title')}
            align="center"
            subtitle={t('ourProcess.subtitle')}
            size="medium"
            glowEffect
          />
        </Box>
      </Fade>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Main Process Diagram */}
          <Box sx={{ position: 'relative', mb: 8 }}>
        {/* Single Continuous Connection Line */}
        <Box
          sx={{
            position: 'absolute',
            top: '70px',
            [isRTL ? 'right' : 'left']: { xs: '50%', md: '10%' },
            width: { xs: 0, md: '80%' },
            height: 2,
            background: 'linear-gradient(90deg, #00d4ff 0%, #00ff88 25%, #ff0080 50%, #ffaa00 75%, #00d4ff 100%)',
            opacity: 0.6,
            zIndex: 0,
            display: { xs: 'none', md: 'block' },
            transform: { xs: 'none', md: isRTL ? 'scaleX(-1)' : 'none' },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: 0,
              width: '100%',
              height: '100%',
              background: 'inherit',
              filter: 'blur(8px)',
              transform: 'translateY(-50%)',
            }
          }}
        />

        {/* Process Steps Circle */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' },
            gap: { xs: 4, md: 2 },
            position: 'relative',
            zIndex: 1,
          }}
        >
          {processSteps.map((step, index) => (
            <Zoom in timeout={500 + index * 100} key={step.id || index}>
              <Box
                onMouseEnter={() => setHoveredStep(index)}
                onMouseLeave={() => setHoveredStep(null)}
                onClick={() => setSelectedStep(index)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                {/* Step Number */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: { xs: -10, md: -15 },
                    [isRTL ? 'left' : 'right']: { xs: '50%', md: '25%' },
                    transform: {
                      xs: `translateX(${isRTL ? '-50%' : '50%'})`,
                      md: 'translateX(0)'
                    },
                    bgcolor: step.color,
                    color: 'white',
                    width: { xs: 24, sm: 28, md: 30 },
                    height: { xs: 24, sm: 28, md: 30 },
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.825rem', md: '0.875rem' },
                    boxShadow: `0 4px 20px ${step.color}40`,
                    zIndex: 2,
                  }}
                >
                  {isRTL ? (processSteps.length - index) : (step.step_number || index + 1)}
                </Box>

                {/* Icon Circle */}
                <Paper
                  elevation={hoveredStep === index || selectedStep === index ? 8 : 3}
                  sx={{
                    width: { xs: 100, sm: 120, md: 140 },
                    height: { xs: 100, sm: 120, md: 140 },
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                      hoveredStep === index || selectedStep === index
                        ? `linear-gradient(135deg, ${step.color}20, ${step.color}10)`
                        : mode === 'light' ? 'oklch(98.7% 0.026 102.212)' : '#111827',
                    border: `3px solid ${
                      selectedStep === index ? step.color : 'transparent'
                    }`,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform:
                      hoveredStep === index || selectedStep === index
                        ? 'translateY(-10px) scale(1.1)'
                        : 'translateY(0) scale(1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background: `radial-gradient(circle at center, ${step.color}10, transparent)`,
                      opacity: hoveredStep === index || selectedStep === index ? 1 : 0,
                      transition: 'opacity 0.3s',
                    },
                  }}
                >
                  <Box
                    sx={{
                      color: step.color,
                      transform:
                        hoveredStep === index || selectedStep === index
                          ? 'scale(1.2)'
                          : 'scale(1)',
                      transition: 'transform 0.3s',
                      filter: `drop-shadow(0 0 20px ${step.color}40)`,
                      '& svg': {
                        fontSize: { xs: 32, sm: 36, md: 40 },
                      },
                    }}
                  >
                    {getIconComponent(step.icon_name)}
                  </Box>
                </Paper>

                {/* Title */}
                <Typography
                  variant="h6"
                  sx={{
                    mt: 2,
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                    color:
                      hoveredStep === index || selectedStep === index
                        ? step.color
                        : mode === 'light' ? 'rgba(51,51,51,0.9)' : 'rgba(240,244,248,0.9)',
                    transition: 'color 0.3s',
                    textAlign: 'center',
                    px: 1,
                  }}
                >
                  {step.title}
                </Typography>

              </Box>
            </Zoom>
          ))}
        </Box>
          </Box>

          {/* Selected Step Details */}
          {processSteps.length > 0 && (
            <Fade in key={selectedStep} timeout={600}>
        <Paper
          elevation={6}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            background: mode === 'light' ? 'oklch(98.7% 0.026 102.212)' : '#111827',
            border: `2px solid ${processSteps[selectedStep].color}20`,
            boxShadow: `none`,
            position: 'relative',
            overflow: 'hidden',
              cursor: 'grab',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: { xs: -50, md: -100 },
              [isRTL ? 'left' : 'right']: { xs: -50, md: -100 },
              width: { xs: 200, sm: 250, md: 300 },
              height: { xs: 200, sm: 250, md: 300 },
              borderRadius: '50%',
              background: `radial-gradient(circle, ${processSteps[selectedStep].color}10, transparent)`,
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
              flexDirection: isRTL ? 'row-reverse' : 'row',
            }}>
              <Box sx={{ color: processSteps[selectedStep].color }}>
                {getIconComponent(processSteps[selectedStep].icon_name)}
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: processSteps[selectedStep].color,
                  textAlign: isRTL ? 'right' : 'left',
                  fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
                }}
              >
                {processSteps[selectedStep].title}
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'light' ? 'rgba(51,51,51,0.8)' : 'rgba(240,244,248,0.8)',
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                textAlign: isRTL ? 'right' : 'left',
                direction: isRTL ? 'rtl' : 'ltr',
              }}
            >
              {processSteps[selectedStep].description}
            </Typography>
          </Box>

          {/* Decorative Elements */}
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: -30, md: -50 },
              [isRTL ? 'right' : 'left']: { xs: -30, md: -50 },
              width: { xs: 100, sm: 120, md: 150 },
              height: { xs: 100, sm: 120, md: 150 },
              borderRadius: '50%',
              border: `3px solid ${processSteps[selectedStep].color}20`,
            }}
          />
            </Paper>
          </Fade>
          )}

      {/* Bottom CTA */}
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Typography
          variant="h5"
          sx={{
            color: mode === 'light' ? 'rgba(51,51,51,0.8)' : 'rgba(240,244,248,0.8)',
            mb: 2,
            fontWeight: 600,
          }}
        >
          {t('ourProcess.cta.title')}
        </Typography>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          flexDirection: isRTL ? 'row-reverse' : 'row',
        }}>
          <TrendingUp sx={{ color: '#00d4ff' }} />
          <Typography variant="body1" sx={{ color: mode === 'light' ? 'rgba(51,51,51,0.6)' : 'rgba(240,244,248,0.6)' }}>
            {t('ourProcess.cta.subtitle')}
          </Typography>
        </Box>
        </Box>
      </>
      )}
    </Container>
  );
};

export default OurProcess;
